/**
 * TAB 6 — Daily Report (requirement §46 to §50, AC-20, AC-21).
 *
 * Report history, the rendered report, email delivery status with retry, and
 * the reconciliation exception queue.
 *
 * Reconciliation lives here rather than in its own tab because §47 Section H
 * reports it as part of the daily close, and AC-02 fixes the module at seven
 * tabs.
 */

import * as React from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import {
  DataTable,
  StatusChip,
  SectionTitle,
  ErrorBar,
  Loading,
  fmtVC,
  fmtDateTime,
  humanise,
  filterInputSx,
  dialogSx,
  primaryBtnSx,
  secondaryBtnSx,
  GOLD,
  BORDER,
} from "../components/common";
import {
  getReports,
  getReportDetail,
  generateReport,
  retryReportEmail,
  getReconciliation,
  resolveException,
} from "../api";

export const ReportsTab = () => {
  const [notice, setNotice] = React.useState("");
  const [error, setError] = React.useState("");

  return (
    <Box>
      <ErrorBar error={error} onClose={() => setError("")} />
      {notice && (
        <Alert severity="success" onClose={() => setNotice("")} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}
      <ReportHistory onNotice={setNotice} onError={setError} />
      <Divider sx={{ my: 3, borderColor: BORDER }} />
      <ReconciliationPanel onNotice={setNotice} onError={setError} />
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Report history + detail
// ═════════════════════════════════════════════════════════════════════════════

const ReportHistory = ({ onNotice, onError }) => {
  const [state, setState] = React.useState({ data: [], pagination: null });
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [detail, setDetail] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [generateDate, setGenerateDate] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await getReports({ page, limit });
      setState(result);
    } catch (err) {
      onError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, limit, onError]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (reportId) => {
    try {
      const result = await getReportDetail({ reportId });
      setDetail(result);
    } catch (err) {
      onError(err.message);
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const result = await generateReport({
        date: generateDate || undefined,
        sendEmail: true,
        force: true,
      });
      onNotice(
        result.success
          ? `Report generated for ${result.report.reportDate}.`
          : `Generation failed: ${result.error}`
      );
      await load();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const retry = async (reportId) => {
    setBusy(true);
    try {
      const result = await retryReportEmail(reportId);
      onNotice(result.success ? "Email sent." : `Email retry failed: ${result.error}`);
      await load();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: "reportDate", label: "Report Date" },
    { key: "status", label: "Status", render: (r) => <StatusChip value={r.status} /> },
    { key: "generatedAt", label: "Generated At", render: (r) => fmtDateTime(r.generatedAt) },
    {
      key: "recipients",
      label: "Recipients",
      render: (r) => (r.recipients || []).join(", ") || "-",
    },
    { key: "emailStatus", label: "Email Status", render: (r) => <StatusChip value={r.emailStatus} /> },
    { key: "sentAt", label: "Sent At", render: (r) => fmtDateTime(r.sentAt) },
    {
      key: "failureReason",
      label: "Failure Reason",
      render: (r) => (
        <Typography sx={{ fontSize: "0.68rem", color: "#EF9A9A" }}>
          {r.failureReason || r.emailFailureReason || "-"}
        </Typography>
      ),
    },
    {
      key: "action",
      label: "Action",
      align: "center",
      render: (r) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openDetail(r.reportId);
            }}
            sx={{ color: GOLD, textTransform: "none", fontSize: "0.68rem" }}
          >
            View
          </Button>
          {r.emailStatus === "Failed" && r.status === "Generated" && (
            <Button
              size="small"
              startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
              disabled={busy}
              onClick={(e) => {
                e.stopPropagation();
                retry(r.reportId);
              }}
              sx={{ color: "#FFCC80", textTransform: "none", fontSize: "0.68rem" }}
            >
              Retry
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <SectionTitle
        action={
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              size="small"
              type="date"
              label="Generate for date"
              InputLabelProps={{ shrink: true }}
              value={generateDate}
              onChange={(e) => setGenerateDate(e.target.value)}
              sx={filterInputSx}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              disabled={busy}
              onClick={generate}
              sx={{ color: GOLD, borderColor: BORDER, textTransform: "none" }}
            >
              {busy ? "Working..." : "Generate Now"}
            </Button>
          </Box>
        }
      >
        Daily Report History
      </SectionTitle>

      <DataTable
        dense
        columns={columns}
        rows={state.data}
        loading={loading}
        pagination={state.pagination}
        onPageChange={setPage}
        onLimitChange={setLimit}
        onRowClick={(r) => openDetail(r.reportId)}
        emptyMessage="No reports generated yet. The first runs automatically after the configured reporting time."
      />

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth sx={dialogSx}>
        <DialogTitle>
          Daily House Treasury Report — {detail?.report?.reportDate}
        </DialogTitle>
        {/* The report is the emailed HTML document, which is designed for a
            white page. The colour override is needed because dialogSx sets a
            light body colour for the dark dialogs — on white that would be
            unreadable. */}
        <DialogContent
          dividers
          sx={{ backgroundColor: "#FFF", "&.MuiDialogContent-root": { color: "#222" } }}
        >
          {detail?.html ? (
            <div dangerouslySetInnerHTML={{ __html: detail.html }} />
          ) : (
            <Loading />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDetail(null)} sx={secondaryBtnSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Reconciliation (§55 to §57, AC-22)
// ═════════════════════════════════════════════════════════════════════════════

const ReconciliationPanel = ({ onNotice, onError }) => {
  const [state, setState] = React.useState({ data: [], pagination: null, checkTypes: [], severities: [] });
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [status, setStatus] = React.useState("Open");
  const [severity, setSeverity] = React.useState("all");
  const [checkType, setCheckType] = React.useState("all");
  const [busy, setBusy] = React.useState(false);
  const [resolving, setResolving] = React.useState(null);
  const [note, setNote] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await getReconciliation({ page, limit, status, severity, checkType });
      setState(result);
    } catch (err) {
      onError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, severity, checkType, onError]);

  React.useEffect(() => {
    load();
  }, [load]);

  const resolve = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await resolveException({ exceptionId: resolving.exceptionId, note });
      setResolving(null);
      setNote("");
      onNotice("Exception resolved.");
      await load();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: "exceptionId", label: "Exception ID", width: 160 },
    { key: "checkType", label: "Exception", render: (r) => humanise(r.checkType) },
    { key: "severity", label: "Severity", render: (r) => <StatusChip value={r.severity} /> },
    { key: "accountName", label: "Account", render: (r) => r.accountName || r.accountId || "-" },
    { key: "accountType", label: "Type" },
    {
      key: "expectedBalance",
      label: "Expected",
      align: "right",
      render: (r) => (r.expectedBalance == null ? "-" : fmtVC(r.expectedBalance)),
    },
    {
      key: "actualBalance",
      label: "Actual",
      align: "right",
      render: (r) => (r.actualBalance == null ? "-" : fmtVC(r.actualBalance)),
    },
    {
      key: "difference",
      label: "Difference",
      align: "right",
      render: (r) => (
        <span style={{ color: "#EF9A9A" }}>{r.difference == null ? "-" : fmtVC(r.difference)}</span>
      ),
    },
    {
      key: "description",
      label: "Detail",
      width: 320,
      render: (r) => (
        <Typography sx={{ fontSize: "0.66rem", color: "#BDBDBD" }}>{r.description}</Typography>
      ),
    },
    { key: "status", label: "Status", render: (r) => <StatusChip value={r.status} /> },
    {
      key: "action",
      label: "Action",
      align: "center",
      render: (r) =>
        r.status === "Open" ? (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setResolving(r);
            }}
            sx={{ color: GOLD, textTransform: "none", fontSize: "0.68rem" }}
          >
            Resolve
          </Button>
        ) : (
          <Typography sx={{ fontSize: "0.66rem", color: "#666" }}>
            {r.resolvedByName || "-"}
          </Typography>
        ),
    },
  ];

  return (
    <>
      <SectionTitle
        action={
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <FormControl size="small" sx={filterInputSx}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="Resolved">Resolved</MenuItem>
                <MenuItem value="Ignored">Ignored</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={filterInputSx}>
              <InputLabel>Severity</InputLabel>
              <Select label="Severity" value={severity} onChange={(e) => { setSeverity(e.target.value); setPage(1); }}>
                <MenuItem value="all">All</MenuItem>
                {(state.severities || []).map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ ...filterInputSx, minWidth: 200 }}>
              <InputLabel>Check</InputLabel>
              <Select label="Check" value={checkType} onChange={(e) => { setCheckType(e.target.value); setPage(1); }}>
                <MenuItem value="all">All Checks</MenuItem>
                {(state.checkTypes || []).map((c) => (
                  <MenuItem key={c} value={c}>{humanise(c)}</MenuItem>
                ))}
              </Select>
            </FormControl>

          </Box>
        }
      >
        Reconciliation Exceptions
      </SectionTitle>

      <DataTable
        dense
        columns={columns}
        rows={state.data}
        loading={loading}
        pagination={state.pagination}
        onPageChange={setPage}
        onLimitChange={setLimit}
        emptyMessage="No exceptions. Wallet balances agree with the ledger."
      />

      <Typography sx={{ color: "#666", fontSize: "0.66rem", mt: 1 }}>
        Reconciliation never alters a balance — it records disagreements for investigation. A
        failing check does not freeze the treasury.
      </Typography>

      <Dialog open={!!resolving} onClose={() => !busy && setResolving(null)} maxWidth="sm" fullWidth sx={dialogSx}>
        <DialogTitle>Resolve Exception</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.8rem", mb: 2 }}>{resolving?.description}</Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            required
            label="Resolution note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            helperText="Recorded permanently in the audit log. This marks the finding reviewed; it does not change any balance."
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setResolving(null)} disabled={busy} sx={secondaryBtnSx}>
            Cancel
          </Button>
          <Button onClick={resolve} disabled={busy || !note.trim()} variant="contained" sx={primaryBtnSx}>
            {busy ? "Saving..." : "Resolve"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportsTab;
